import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import TagManager from 'react-gtm-module';

import dayjs from 'dayjs';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { CookieConsentPure } from 'component/CookieConsent/CookieConsentPure';
import { getGTMKey } from 'app/ga/gaKeys';
import { FeatureFlags } from 'config/growthbook/featureFlags';
import { TRACKING_CONSENT_COOKIE } from 'shared/constants/cookies';
import { useIsCookieConsentVisible } from 'shared/hooks/useIsCookieConsentVisible';

export type CookieConsentProps = {};

export const CookieConsent: React.VFC<CookieConsentProps> = () => {
  const [cookies, setCookie] = useCookies([TRACKING_CONSENT_COOKIE]);
  const expires = dayjs().add(1, 'year').toDate();
  const isCookieConsentVisible = useIsCookieConsentVisible();
  const isCookieConsentAccepted = String(cookies[TRACKING_CONSENT_COOKIE]) === '1';
  const isRUMEnabled = useFeatureIsOn(FeatureFlags.DATADOG_RUM);
  const isLogsEnabled = useFeatureIsOn(FeatureFlags.DATADOG_LOGS_FE);

  useEffect(() => {
    if (isCookieConsentAccepted) {
      // The `datadog-rum` flag is the RUM kill switch: RUM initializes at app
      // entry but only starts sending once consent is granted, so withholding
      // the grant here keeps it fully off. Stopping an already-consented
      // session instead would not stick — the SDK renews it on user activity.
      if (isRUMEnabled) {
        datadogRum.setTrackingConsent('granted');
      }

      // Datadog Logs is initialized with trackingConsent: 'not-granted', so grant it here
      // once the respondent consents. The datadog-logs-fe flag also acts as a kill switch.
      if (isLogsEnabled) {
        datadogLogs.setTrackingConsent('granted');
      }

      if (getGTMKey()) {
        TagManager.initialize({
          gtmId: getGTMKey(),
        });
      }
    }
  }, [isCookieConsentAccepted, isRUMEnabled, isLogsEnabled]);

  return (
    <>
      {isCookieConsentVisible && (
        <CookieConsentPure
          onAccept={() => {
            setCookie(TRACKING_CONSENT_COOKIE, '1', { expires, sameSite: 'none', secure: true });
          }}
          onReject={() => {
            setCookie(TRACKING_CONSENT_COOKIE, '0', { expires, sameSite: 'none', secure: true });
          }}
        />
      )}
    </>
  );
};
